/**
 * MCP Tool Selector Field Component
 * 
 * Specialized multi-select for MCP tools that fetches available tools
 * from the MCP server when the dropdown is opened
 */

import { useState, useEffect } from 'react';
import { Label } from '@/components/ui/label';
import { MultiSelect } from '@/components/ui/multiselect';
import { mcpApi } from '@/lib/api';
import type { InferredPropertySchema } from '@/types/inferred-schema';
import { HelpCircle, RefreshCw, AlertCircle } from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { Button } from '@/components/ui/button';
import { useReactFlow } from 'reactflow';

interface MCPToolSelectorFieldProps {
  property: InferredPropertySchema;
  value: string[];
  onChange: (value: string[]) => void;
  error?: string;
  nodeId?: string;
}

interface MCPTool {
  name: string;
  description?: string;
}

export function MCPToolSelectorField({ 
  property, 
  value, 
  onChange, 
  error,
  nodeId 
}: MCPToolSelectorFieldProps) {
  
  const [tools, setTools] = useState<MCPTool[]>([]);
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [hasFetched, setHasFetched] = useState(false);
  const { getNode } = useReactFlow();

  // Ensure value is always an array, default to ['*'] (all tools)
  const selectedTools = Array.isArray(value) ? value : ['*'];

  const fetchTools = async () => {
    if (!nodeId) return;

    setLoading(true);
    setFetchError(null);

    try {
      const node = getNode(nodeId);
      if (!node) {
        throw new Error('Node not found');
      }

      const nodeData = node.data;
      const parameters = nodeData.properties || nodeData.parameters || {};


      // Get connection details from node parameters
      const transportType = parameters.transportType || 'http';
      const mcpServerUrl = parameters.mcpServerUrl;
      const authType = parameters.authType || 'none';
      const bearerToken = parameters.bearerToken;
      const customHeaderName = parameters.customHeaderName;
      const customHeaderValue = parameters.customHeaderValue;
      const stdioCommand = parameters.stdioCommand;
      const stdioArgs = parameters.stdioArgs;
      const stdioEnv = parameters.stdioEnv;
      const sseEnv = parameters.sseEnv;
      const httpEnv = parameters.httpEnv;

      // console.log('🔍 MCPToolSelectorField - Extracted values:', {
      //   transportType,
      //   mcpServerUrl,
      //   stdioCommand,
      //   stdioArgs,
      //   stdioEnv,
      //   sseEnv,
      //   httpEnv
      // });

      // Validate required fields
      if (!mcpServerUrl && (transportType === 'http' || transportType === 'http-simple' || transportType === 'sse')) {
        throw new Error('MCP Server URL is required. Please configure the connection settings first.');
      }

      if (!stdioCommand && transportType === 'stdio') {
        throw new Error('STDIO Command is required. Please configure the STDIO settings first.');
      }

      // Call backend proxy to list tools (avoids CORS issues)
      const response = await mcpApi.listTools({
        nodeId: nodeId!, // Pass node ID to prefix tool names
        mcpServerUrl,
        transportType,
        authType,
        authToken: authType === 'bearer' ? bearerToken : authType === 'header' ? customHeaderValue : undefined,
        stdioCommand,
        stdioArgs,
        stdioEnv,
      });

      const data = response.data;

      if (!data.success) {
        throw new Error(data.error || 'Failed to fetch tools from MCP server');
      }

      if (!Array.isArray(data.tools)) {
        throw new Error('Invalid response format from backend');
      }

      setTools(data.tools);
      setHasFetched(true);
    } catch (err) {
      // console.error('Error fetching MCP tools:', err);
      setFetchError(err instanceof Error ? err.message : 'Failed to fetch tools');
    } finally {
      setLoading(false);
    }
  };

  // Auto-fetch tools on mount if there are specific tools selected (not just '*')
  // This ensures the UI shows the selected tool names when reopening the editor
  useEffect(() => {
    const hasSpecificToolsSelected = selectedTools.length > 0 && !selectedTools.includes('*');
    if (hasSpecificToolsSelected && !hasFetched && !loading && nodeId) {
      fetchTools();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nodeId]); // Only run on mount (when nodeId is set)

  // Convert tools to options format for MultiSelect
  const options = [
    { value: '*', label: 'All Tools (Default)' },
    ...tools.map(tool => ({
      value: tool.name,
      label: tool.description ? `${tool.name} - ${tool.description}` : tool.name
    }))
  ];

  // Handle value change
  const handleChange = (newValue: string[]) => {
    // If '*' was already selected and user clicked a specific tool, remove '*' and keep just the specific tool(s)
    if (selectedTools.includes('*') && newValue.length > 1) {
      onChange(newValue.filter(v => v !== '*'));
    }
    // If '*' was just added (user clicked "All Tools"), select only '*'
    else if (newValue.includes('*') && !selectedTools.includes('*')) {
      onChange(['*']);
    }
    // If nothing selected, default to '*'
    else if (newValue.length === 0) {
      onChange(['*']);
    }
    // Otherwise, keep the selection as-is
    else {
      onChange(newValue);
    }
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Label htmlFor={property.key}>
            {property.label}
            {property.required && <span className="text-destructive ml-1">*</span>}
          </Label>
          {property.description && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <HelpCircle className="h-4 w-4 text-muted-foreground cursor-help" />
                </TooltipTrigger>
                <TooltipContent className="max-w-xs">
                  <p className="text-sm">{property.description}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </div>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={fetchTools}
          disabled={loading}
          className="h-8 px-2"
        >
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          <span className="ml-1 text-xs">
            {loading ? 'Loading...' : hasFetched ? 'Refresh' : 'Load Tools'}
          </span>
        </Button>
      </div>

      {fetchError && (
        <div className="flex items-start gap-2 p-3 text-sm bg-destructive/10 border border-destructive/20 rounded-md">
          <AlertCircle className="h-4 w-4 text-destructive mt-0.5 flex-shrink-0" />
          <p className="text-destructive">{fetchError}</p>
        </div>
      )}

      {!hasFetched && !loading && !fetchError && (
        <div className="p-3 text-sm bg-muted border rounded-md text-muted-foreground">
          Click "Load Tools" to fetch available tools from the MCP server. Make sure to configure the connection settings first.
        </div>
      )}

      {(hasFetched || loading) && (
        <MultiSelect
          options={options}
          value={selectedTools}
          onValueChange={handleChange}
          placeholder={loading ? 'Loading tools...' : 'Select tools to expose'}
          emptyMessage={loading ? 'Loading...' : 'No tools available'}
          maxDisplay={3}
        />
      )}

      {error && <p className="text-sm text-destructive">{error}</p>}

      {selectedTools.includes('*') && hasFetched && tools.length > 0 && (
        <p className="text-xs text-muted-foreground">
          All {tools.length} tools will be available to AI agents
        </p>
      )}
      
      {!selectedTools.includes('*') && selectedTools.length > 0 && (
        <p className="text-xs text-muted-foreground">
          {selectedTools.length} tool{selectedTools.length !== 1 ? 's' : ''} selected
        </p>
      )}
    </div>
  );
}
